import { createRouter, createWebHistory } from "vue-router";
import LandingView from "./views/LandingView.vue";
import MyPage from "./views/MyPage.vue";
import PlanView from "./views/PlanView.vue";
import ShioriView from "./views/ShioriView.vue";
import ScheduleView from "./views/ScheduleView.vue";
import NotesView from "./views/NotesView.vue";
import WarikanView from "./views/WarikanView.vue";
import LoginView from "@/views/LoginView.vue";
import AuthCallback from "@/views/AuthCallback.vue";
import TrashView from "./views/TrashView.vue";
import SharedShioriView from "./views/SharedShioriView.vue";
import ResetPasswordView from "./views/ResetPasswordView.vue";
import AccountRecoveryView from "./views/AccountRecoveryView.vue";
import AdminView from "./views/AdminView.vue";
import AiTripConsultView from "./views/AiTripConsultView.vue";
import DestinationBoardView from "./views/DestinationBoardView.vue";
import AiDestinationSuggestView from "./views/AiDestinationSuggestView.vue";
import TrialCreateView from "./views/TrialCreateView.vue";
import JoinDestinationBoard from "./views/JoinDestinationBoard.vue";
import TripReviewView from "./views/TripReviewView.vue";
import ReviewDashboardView from "./views/ReviewDashboardView.vue";
import { PUBLIC_CONTENT_ENABLED } from "@/config/public-content";

const DiscoverView = () => import("./views/DiscoverView.vue");
const ModelCoursesView = () => import("./views/ModelCoursesView.vue");
const ModelCourseDetailView = () => import("./views/ModelCourseDetailView.vue");
const RankingsView = () => import("./views/RankingsView.vue");
const RankingDetailView = () => import("./views/RankingDetailView.vue");
const AdminPublicContentsView = () => import("./views/AdminPublicContentsView.vue");
const AdminPublicContentDetailView = () => import("./views/AdminPublicContentDetailView.vue");
const PublicPrivacyView = () => import("./views/PublicPrivacyView.vue");
const PublicSupportView = () => import("./views/PublicSupportView.vue");

const SAFE_LINK_VALUE = /^[A-Za-z0-9_-]+$/;
const isSafeLinkValue = (value, maxLength = 512) =>
  typeof value === "string" &&
  value.length > 0 &&
  value.length <= maxLength &&
  SAFE_LINK_VALUE.test(value);
const hasOnlyQueryKeys = (to, allowedKeys) =>
  Object.keys(to.query).every((key) => allowedKeys.includes(key)) &&
  Object.values(to.query).every((value) => typeof value === "string");
const rejectUnsafeLink = () => ({ name: "Landing" });

const validateSharedFallback = (to) =>
  isSafeLinkValue(to.params.token) && hasOnlyQueryKeys(to, [])
    ? true
    : rejectUnsafeLink();

const validateSharedShiori = (to) =>
  isSafeLinkValue(to.params.token) &&
  hasOnlyQueryKeys(to, ["join"]) &&
  (to.query.join === undefined || to.query.join === "1")
    ? true
    : rejectUnsafeLink();

const validateAccountRecovery = (to) => {
  if (!hasOnlyQueryKeys(to, ["token"])) return rejectUnsafeLink();
  if (to.query.token === undefined) return true;
  return isSafeLinkValue(to.query.token) && to.query.token.length >= 32
    ? true
    : rejectUnsafeLink();
};

const validateTripFallback = (allowedQueryKeys, requiredQueryKeys = []) =>
  (to) => {
    if (
      !isSafeLinkValue(to.params.id, 50) ||
      !hasOnlyQueryKeys(to, allowedQueryKeys)
    ) {
      return rejectUnsafeLink();
    }
    if (!requiredQueryKeys.every((key) => isSafeLinkValue(to.query[key], 50))) {
      return rejectUnsafeLink();
    }
    if (
      allowedQueryKeys.some(
        (key) =>
          to.query[key] !== undefined && !isSafeLinkValue(to.query[key], 50),
      )
    ) {
      return rejectUnsafeLink();
    }
    return true;
  };

const routes = [
  { path: "/discover", name: "Discover", component: DiscoverView, meta: { layout: 'app-shell', requiresAuth: false, publicContent: true, title: "旅のモデルコースとおすすめ", description: "旅行のプロが選んだモデルコースと、根拠の見えるおすすめランキング" } },
  { path: "/discover/model-courses", name: "ModelCourses", component: ModelCoursesView, meta: { layout: 'app-shell', requiresAuth: false, publicContent: true, title: "モデルコース", description: "しおり形式で読める旅行モデルコース" } },
  { path: "/discover/model-courses/:slug", name: "ModelCourseDetail", component: ModelCourseDetailView, meta: { layout: 'app-shell', requiresAuth: false, publicContent: true } },
  { path: "/discover/rankings", name: "Rankings", component: RankingsView, meta: { layout: 'app-shell', requiresAuth: false, publicContent: true, title: "おすすめランキング", description: "評価方法と調査日を明示した旅行ランキング" } },
  { path: "/discover/rankings/:slug", name: "RankingDetail", component: RankingDetailView, meta: { layout: 'app-shell', requiresAuth: false, publicContent: true } },
  { path: "/admin/public-contents", name: "AdminPublicContents", component: AdminPublicContentsView, meta: { layout: 'app-shell', requiresAuth: true, publicContent: true, title: "公開コンテンツ管理" } },
  { path: "/admin/public-contents/:id", name: "AdminPublicContentDetail", component: AdminPublicContentDetailView, meta: { layout: 'app-shell', requiresAuth: true, publicContent: true, title: "公開コンテンツ詳細" } },
  {
    path: "/",
    name: "Landing",
    component: LandingView,
    meta: { layout: 'full', title: "「どこ行く？」から「次どこ行く？」まで", description: "旅のしおりをかんたんに作成、共有、検討できる旅行プランナー" },
  },
  {
    path: "/trial",
    name: "TrialCreate",
    component: TrialCreateView,
    meta: { layout: 'full', requiresAuth: false },
  },
  {
    path: "/trial/:id",
    name: "Trial",
    component: SharedShioriView,
    meta: { layout: 'app-shell', requiresAuth: false },
  },
  {
    path: "/shared/checklist/:token",
    name: "SharedChecklistFallback",
    component: SharedShioriView,
    beforeEnter: validateSharedFallback,
    meta: { layout: 'app-shell', requiresAuth: false, title: "共有されたチェックリスト", description: "共有された旅行のチェックリストを確認できます", sharedInitialTab: "todo", skipAnalytics: true },
  },
  {
    path: "/shared/warikan/:token",
    name: "SharedWarikanFallback",
    component: SharedShioriView,
    beforeEnter: validateSharedFallback,
    meta: { layout: 'app-shell', requiresAuth: false, title: "共有された割り勘", description: "共有された旅行の割り勘を確認できます", sharedInitialTab: "warikan", skipAnalytics: true },
  },
  {
    path: "/shared/:token",
    name: "SharedShiori",
    component: SharedShioriView,
    beforeEnter: validateSharedShiori,
    meta: { layout: 'app-shell', requiresAuth: false, title: "共有されたしおり", description: "共有された旅行のしおりを確認できます", skipAnalytics: true },
  },
  {
    path: "/account-recovery",
    name: "AccountRecovery",
    component: AccountRecoveryView,
    beforeEnter: validateAccountRecovery,
    meta: { layout: 'full', requiresAuth: false, title: "アカウント復元", description: "削除済みアカウントの復元手続きを行います", skipAnalytics: true },
  },
  {
    path: "/reset-password",
    name: "ResetPassword",
    component: ResetPasswordView,
    meta: { layout: 'full', requiresAuth: false, title: "パスワードリセット", description: "アカウントのパスワード再設定を行います", skipAnalytics: true },
  },
  {
    path: "/privacy",
    name: "PublicPrivacy",
    component: PublicPrivacyView,
    meta: { layout: 'full', requiresAuth: false, title: "プライバシーポリシー", description: "RabiiTripの個人情報と旅行計画データの取り扱いについて", skipAnalytics: true },
  },
  {
    path: "/support",
    name: "PublicSupport",
    component: PublicSupportView,
    meta: { layout: 'full', requiresAuth: false, title: "サポート", description: "RabiiTripの問い合わせ、FAQ、アカウント削除の案内", skipAnalytics: true },
  },
  {
    path: "/mypage",
    name: "MyPage",
    component: MyPage,
    meta: { layout: 'app-shell', requiresAuth: true, title: "マイページ", description: "作成したしおりや旅行先検討ボードの一覧を確認できます" },
  },
  {
    path: "/ai-trip",
    name: "AiTripConsult",
    component: AiTripConsultView,
    meta: { layout: 'app-shell', requiresAuth: true, title: "AI旅行相談", description: "AIに旅行のプランや行き先についての相談ができます" },
  },
  {
    path: "/destination-boards",
    name: "DestinationBoards",
    component: MyPage,
    meta: { layout: 'app-shell', requiresAuth: true, title: "旅行先を選ぼう", description: "旅行先検討ボードの一覧を確認できます" },
  },
  {
    path: "/destination-boards/join/:token",
    name: "JoinDestinationBoard",
    component: JoinDestinationBoard,
    beforeEnter: validateSharedFallback,
    meta: { layout: 'full', requiresAuth: false, title: "検討ボードへの参加", description: "招待リンクから旅行先検討ボードに参加します", skipAnalytics: true },
  },
  {
    path: "/destination-board/:id",
    name: "DestinationBoardView",
    component: DestinationBoardView,
    meta: { layout: 'app-shell', requiresAuth: false, title: "旅行先検討ボード", description: "同行者と一緒に複数ある旅行先候補を比較・検討して計画を立てましょう" },
  },
  {
    path: "/ai-destination-suggest",
    name: "AiDestinationSuggestView",
    component: AiDestinationSuggestView,
    meta: { layout: 'app-shell', requiresAuth: true, title: "AI旅行先提案", description: "旅行の時期や予算を入力し、AIから最適な旅行先の提案を受け取ります" },
  },
  {
    path: "/trash",
    name: "TrashView",
    component: TrashView,
    meta: { layout: 'app-shell', requiresAuth: true, title: "ゴミ箱", description: "削除したしおりを復元または完全に削除します" },
  },
  {
    path: "/plan",
    name: "PlanView",
    component: PlanView,
    meta: { layout: 'app-shell', title: "旅行計画作成", description: "新しい旅行計画やしおりを作成します" },
  },
  {
    path: "/shiori/:id",
    name: "ShioriView",
    component: ShioriView,
    meta: { layout: 'app-shell', title: "しおり詳細", description: "しおりの確認とメンバー管理を行います" },
  },
  {
    path: "/trips/:id",
    name: "TripHubFallback",
    component: ShioriView,
    beforeEnter: validateTripFallback([]),
    meta: { layout: 'app-shell', requiresAuth: true, title: "しおり詳細", description: "しおりの確認とメンバー管理を行います", skipAnalytics: true },
  },
  {
    path: "/trips/:id/schedule",
    name: "TripScheduleFallback",
    component: ScheduleView,
    beforeEnter: validateTripFallback(["schedule"]),
    meta: { layout: 'app-shell', requiresAuth: true, title: "旅行スケジュール", description: "旅行スケジュールの日程調整やイベント作成を行います", skipAnalytics: true },
  },
  {
    path: "/trips/:id/checklist",
    name: "TripChecklistFallback",
    component: ShioriView,
    beforeEnter: validateTripFallback(["page", "item"], ["page", "item"]),
    meta: { layout: 'app-shell', requiresAuth: true, title: "旅行チェックリスト", description: "旅行のチェックリストを確認できます", initialTab: "todo", skipAnalytics: true },
  },
  {
    path: "/trips/:id/warikan",
    name: "TripWarikanFallback",
    component: WarikanView,
    beforeEnter: validateTripFallback(["expense"], ["expense"]),
    meta: { layout: 'app-shell', requiresAuth: true, title: "割り勘精算", description: "旅行費用の入力とメンバー間の割り勘計算を行います", skipAnalytics: true },
  },
  {
    path: "/reviews",
    name: "ReviewDashboard",
    component: ReviewDashboardView,
    meta: { layout: 'app-shell', requiresAuth: true, title: "評価ダッシュボード", description: "旅行評価の集計・好み分析と次の旅行先提案" },
  },
  {
    path: "/shiori/:shioriId/review",
    name: "TripReviewView",
    component: TripReviewView,
    meta: { layout: 'app-shell', requiresAuth: true, title: "次の旅行のヒントに", description: "旅行後の振り返りを記録します" },
  },
  {
    path: "/schedule",
    name: "ScheduleView",
    component: ScheduleView,
    meta: { layout: 'app-shell', title: "旅行スケジュール", description: "旅行スケジュールの日程調整やイベント作成を行います" },
  },
  {
    path: "/warikan/:id",
    name: "WarikanView",
    component: WarikanView,
    meta: { layout: 'app-shell', title: "割り勘精算", description: "旅行費用の入力とメンバー間の割り勘計算を行います" },
  },
  {
    path: "/note",
    name: "NotesView",
    component: NotesView,
    meta: { layout: 'app-shell', title: "旅行メモ・持ち物リスト", description: "旅行の準備用チェックリストとメモの管理を行います" },
  },
  {
    path: "/login",
    name: "Login",
    component: LoginView,
    meta: { layout: 'full', title: "ログイン", description: "アカウントにログインして旅の計画を始めましょう", skipAnalytics: true },
  },
  {
    path: "/auth/callback",
    alias: "/auth/google/callback",
    name: "AuthCallback",
    component: AuthCallback,
    meta: { layout: 'full', title: "認証処理中", description: "外部認証アカウントのログイン処理を行っています", skipAnalytics: true },
  },
  {
    path: "/admin",
    name: "AdminView",
    component: AdminView,
    meta: { layout: 'app-shell', requiresAuth: true, title: "管理者ページ", description: "サイトのコンテンツ管理や管理者設定を行います" },
  },
];

const hasExplicitScrollTarget = (to) => {
  return Boolean(to.hash || to.query.schedule);
};

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to) {
    if (hasExplicitScrollTarget(to)) {
      return false;
    }

    return { top: 0, left: 0 };
  },
});

// 認証ガード
router.beforeEach((to, from, next) => {
  if (to.meta.publicContent && !PUBLIC_CONTENT_ENABLED) {
    next("/");
    return;
  }
  const token = localStorage.getItem("token");

  // ログインページにアクセスしようとした場合
  if (to.path === "/login") {
    // すでにログイン済みならマイページへリダイレクト
    if (token) {
      next("/mypage");
      return;
    }
  }

  // 認証が必要なページへのアクセス
  if (to.name === "DestinationBoardView") {
    next();
    return;
  }

  if (to.meta.requiresAuth && !token) {
    next("/login");
    return;
  }

  next();
});

// SEO対策：ページ遷移完了時にタイトルとメタ説明を動的に更新
router.afterEach((to) => {
  const title = to.meta.title ? `${to.meta.title} | RabiiTrip` : "RabiiTrip";
  document.title = title;

  const description = to.meta.description || "候補地選びから旅の振り返りまで。みんなの好みが貯まる旅行共同編集アプリ";
  let metaDesc = document.querySelector('meta[name="description"]');
  if (!metaDesc) {
    metaDesc = document.createElement('meta');
    metaDesc.setAttribute('name', 'description');
    document.head.appendChild(metaDesc);
  }
  metaDesc.setAttribute("content", description);

  const shouldSkipPageView = Boolean(to.meta.skipAnalytics);

  if (
    !shouldSkipPageView &&
    typeof window !== "undefined" &&
    typeof window.gtag === "function"
  ) {
    window.gtag("event", "page_view", {
      page_location: `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash || ""}`,
      page_path: to.fullPath,
      page_title: title,
    });
  }

  window.setTimeout(() => {
    const main = document.querySelector("main");
    if (main) {
      main.setAttribute("tabindex", "-1");
      main.focus({ preventScroll: true });
    }
  }, 0);
});

export default router;
